import { isEqual } from "lodash";
import { useEffect, useEffectEvent, useState, useTransition } from "react";
import { Col, Form, Row } from "react-bootstrap";
import {
  IRequestStatus,
  IValidReturn,
  IValidationFunction,
  IValidationTarget,
  IValidationTargetOptions,
  loadWrapper,
} from "../utility";
import { InTextLink } from "./InTextLink";

/**
 * Props for the Control211 component.
 */
export interface Control211Props {
  id: string;
  contentSize?: boolean;
  disabled?: boolean;
  invalidMessage?: string;
  label: string;
  labelWidth?: number | "auto";
  maxWidthCols?: number;
  multiple?: boolean;
  options: string[] | { value: string; label: string }[];
  setValue?: (ret: string) => void;
  subText?: string;
  title?: string;
  validCondition?: IValidationFunction;
  validMessage?: string;
  validationTargetOptions?: IValidationTargetOptions;
  validationRequestUrl?: string;
  validationReturn?: (ret: boolean) => void;
  value: string;
}

/**
 * A select dropdown control that supports both simple string options and complex value-label option objects.
 * Features responsive Bootstrap layout, automatic value synchronization, and optional validation.
 *
 * @param props - Component props.
 * @param props.id - Unique identifier for the select control
 * @param props.contentSize - Whether the control should size to its content
 * @param props.disabled - Whether the select is disabled
 * @param props.invalidMessage - Message to display when validation fails
 * @param props.label - Label text for the select field
 * @param props.labelWidth - Width of the label column in Bootstrap grid units or 'auto' (defaults to 2)
 * @param props.maxWidthCols - Maximum width in Bootstrap grid columns (defaults to 12)
 * @param props.multiple - Whether multiple selection is allowed.  Selections are joined by '|' in the value.
 * @param props.options - Array of options (strings or {value, label} objects)
 * @param props.setValue - Callback function to update the selected value
 * @param props.subText - Additional help text displayed below the select
 * @param props.title - Tooltip text for the control
 * @param props.validCondition - Validation function to check the selected value
 * @param props.validMessage - Message to display when validation passes
 * @param props.validationTargetOptions - Optional validation context options forwarded to the validation function
 * @param props.validationRequestUrl - URL for validation requests (if applicable)
 * @param props.validationReturn - Callback to receive validation results
 * @param props.value - Currently selected value
 * @returns A React functional component rendering a Form.Group with select dropdown
 */
export const Control211 = ({
  id,
  contentSize = false,
  disabled,
  invalidMessage,
  label,
  labelWidth = 2,
  maxWidthCols = 12,
  multiple,
  options,
  setValue,
  subText,
  title,
  validCondition,
  validMessage,
  validationTargetOptions,
  validationRequestUrl = "",
  validationReturn,
  value,
}: Control211Props): React.ReactElement => {
  // Run validation check on current value
  const [pending, startTransition] = useTransition();
  const [validationStatus, setValidationStatus] = useState<IValidReturn>({
    success: false,
    errorText: "",
  });
  const [validationRequest, setValidationRequest] = useState<IRequestStatus<IValidationTarget>>({
    requesting: false,
    error: false,
  });

  /**
   * Checks validation for the current value.
   *
   * @param newValue - The validation target containing the value to check
   */
  const checkValidation = useEffectEvent(async (newValue: IValidationTarget) => {
    if (!validCondition) {
      startTransition(() => {
        setValidationStatus({
          success: true,
          data: {
            valid: true,
            validationText: "",
          },
        });
        setValidationRequest({ requesting: false, error: false, requestedId: newValue });
      });
    } else {
      const response = await loadWrapper(
        validCondition,
        newValue,
        { ...validationRequest, error: false, errorText: undefined },
        setValidationRequest,
        validationRequestUrl,
      );
      /* istanbul ignore else */
      if (response) {
        startTransition(() => setValidationStatus(response));
        validationReturn?.(response.success === false ? false : response.data.valid);
      }
    }
  });

  useEffect(() => {
    if (
      !validationRequest.requesting &&
      (value !== validationRequest.requestedId?.value ||
        !isEqual(validationTargetOptions, validationRequest.requestedId?.options))
    ) {
      checkValidation({ value, options: validationTargetOptions });
    }
  }, [validationRequest, validationTargetOptions, value]);

  return (
    <Form.Group
      controlId={`${id}`}
      as={Row}
      className="my-1"
      title={title}
    >
      {labelWidth !== 0 && (
        <Form.Label
          column
          md={labelWidth}
        >
          {label}
        </Form.Label>
      )}
      <Col
        md={
          labelWidth === 0
            ? maxWidthCols
            : labelWidth === "auto"
              ? undefined
              : maxWidthCols - labelWidth
        }
      >
        <Form.Select
          multiple={multiple}
          style={{
            minHeight: multiple ? "120px" : undefined,
            fieldSizing: contentSize ? "content" : undefined,
          }}
          size="sm"
          value={multiple ? (value ? value.split("|") : []) : value}
          disabled={disabled || !setValue}
          onChange={(e) =>
            setValue?.(
              multiple
                ? Array.from(e.currentTarget.selectedOptions, (option) => option.value).join("|")
                : e.currentTarget.value,
            )
          }
          aria-label={label}
          className={`${
            validationStatus.success && !validationRequest.requesting
              ? validationStatus.data.valid === true
                ? "is-valid"
                : "is-invalid"
              : ""
          }`}
        >
          {options
            .map((o) => (typeof o === "string" ? { value: o, label: o } : o))
            .map((o, i) => (
              <option
                key={i}
                value={o.value}
              >
                {o.label}
              </option>
            ))}
        </Form.Select>
        {(subText || validCondition) && (
          <Form.Text
            style={{ minHeight: "1.5em", display: "block" }}
            className={`${
              validationStatus.success && !validationRequest.requesting
                ? validationStatus.data?.valid === true
                  ? "valid-feedback"
                  : "invalid-feedback"
                : "text-muted"
            }`}
          >
            <InTextLink
              text={[
                subText ?? "",
                validationRequest.requesting || pending
                  ? "updating status..."
                  : validationStatus.success
                    ? validationStatus.data.validationText !== ""
                      ? validationStatus.data.validationText
                      : validationStatus.data.valid === true
                        ? (validMessage ??
                          // istanbul ignore next
                          "")
                        : (invalidMessage ??
                          // istanbul ignore next
                          "")
                    : "",
              ]
                .filter((f) => f !== "")
                .join(" ")}
            />
          </Form.Text>
        )}
      </Col>
    </Form.Group>
  );
};

Control211.displayName = "Control211";
